// ─────────────────────────────────────────────────────────
// lib/queryClient.ts — Centralised TanStack Query client
//
// Phase 2: Setup only — no queries wired yet (that's Phase 3).
// Imported by main.tsx and used by QueryClientProvider.
//
// Defaults:
//   staleTime  5 min  — data considered fresh for 5 min after fetch
//   gcTime    10 min  — unused cache cleared after 10 min
//   retry      2      — retry failed queries up to 2 times
//   refetchOnWindowFocus false — don't re-fetch when tab regains focus
//
// Auth errors (401/403) are not retried — fail-fast.
// ─────────────────────────────────────────────────────────
import { QueryClient } from '@tanstack/react-query'

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase()
    // Auth / permission errors — don't retry
    if (msg.includes('auth') || msg.includes('permission') || msg.includes('unauthorized')) {
      return false
    }
    // Network gone — retry up to 2× with back-off handled by TanStack Query
    if (msg.includes('network') || msg.includes('fetch') || msg.includes('offline')) {
      return failureCount < 2
    }
  }
  return failureCount < 2
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:            5 * 60 * 1000,   // 5 min
      gcTime:              10 * 60 * 1000,   // 10 min
      retry:                shouldRetry,
      refetchOnWindowFocus: false,
      refetchOnReconnect:   true,             // re-fetch when net reconnects
    },
    mutations: {
      retry: 1,
    },
  },
})
